<script setup>
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useCustomerStore } from "@/stores/CustomersStore"
import { useModalStore } from '@/stores/ModalStore'
import { ref, onMounted, onUnmounted, nextTick, onBeforeUnmount } from 'vue'
import CustomerHistoryVisits from "@/components/event/CustomerHistoryVisits.vue"
import FileUploader from "@/components/FileUploader.vue"
import CustomerData from "@/components/event/CustomerData.vue"
import TicketsTable from "@/components/event/TicketsTable.vue"
import VouchersTable from "@/components/event/VouchersTable.vue"
import SectionDivider from "@/components/SectionDivider.vue"
import CustomerNameSurnamePhone from "@/components/customer/CustomerNameSurnamePhone.vue"
import UserStatistics from "@/components/event/UserStatistics.vue"
import { formatPhone } from "@/utils/strings"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useEventStore } from "@/stores/EventsStore"
import { useGeneralSearch } from "@/stores/GeneralSearchEngineStore"
import EditDateDialog from "@/components/dialogs/EditDateDialog.vue"
import BeautyDocTab from "@/components/event/BeautyDocTab.vue"
import ConversationThread from "@/components/messages/ConversationThread.vue"
import { userColor } from "@/utils/colors"

const { t } = useI18n()
// Stores
const customerStore = useCustomerStore()
const calendarStore = useCalendarStore()
const searchStore = useGeneralSearch()
const THIS_MODAL_NAME = 'customerCardDialog'
const modalStore = useModalStore()
const eventStore = useEventStore()
const { mobile } = useDisplay()
const route = useRoute()
const router = useRouter()

const tabs = ref(0) // Default to first tab (data)

const tabRefs = Array.from({ length: 7 }, () => ref(null)) // refs for 7 tabs

// Function to focus on the tab content when tab changes
const focusTab = (newTab) => {
  nextTick(() => {
    tabRefs[newTab]?.value?.$el.focus()
  })
}

const customerDataComponent = ref(null) // ref to component that holds all customer fields
const localCustomerData = ref({}) // Local copy of customer data for editing

// Add a localStorage persistence function
// const saveActiveTab = (tabIndex) => {
//   localStorage.setItem('customerCardActiveTab', tabIndex)
// }
// const getSavedActiveTab = () => {
//   const savedTab = localStorage.getItem('customerCardActiveTab')
//
//   return savedTab ? parseInt(savedTab) : 0
// }
// Save changes made in the CustomerData.vue component form

const submitCustomer = async () => {
  try {
    const { valid } = await customerDataComponent.value.customerDataFormEl?.validate()
    if (valid) {
      // Trigger data submission and validation in the CustomerData component
      const customerData = customerDataComponent.value.getCustomerFormData()

      // console.log('@ Submitting customer in customer card EDIT:', customerData)

      if (customerData) {
        // Update the complete customer information
        const response = await customerStore.updateCustomerInformation(customerData)

        // if we are on calendar -> refetch events cause there might be visits with our changed client
        if (response?.success && route.path === '/calendar') {
          // if event dialog is also opened
          if (modalStore.modals.calendarEvent) {
            calendarStore.addAction(() => {
              //console.warn('action1')
              calendarStore.calendarApi?.refetchEvents()
            }, 'refetchEvents')
          } else {
            calendarStore.calendarApi?.refetchEvents()
          }
        }

      }

      // Reload the customer table - IF ON CUSTOMER TAB
      if (route.path === '/clients') {
        await customerStore.loadCustomers()
      }

    } else {
      console.warn('Customer data form is not valid')
      showToast(t('actions.form.incorrect_filled_field'), 'warning')
    }

  } catch (error) {
    console.error('Error saving customer:', error)
  }
}

// 👉 0. Data
// 👉 1. Attachments
// 👉 2. Visits list
// 👉 3. Vouchers
// 👉 4. Tickets
// 👉 5. Messages - the same conversation as on the Messages page
const conversationClient = computed(() => {
  const { salonerClientId, name, surname, phone } = customerStore.selectedCustomer
  const id = Number(salonerClientId)
  const fullName = `${name ?? ''} ${surname ?? ''}`.trim()

  return { id, name: fullName, phone, color: userColor(fullName, id) }
})

// 👉 Delete customer
const handleCustomerDelete = async () => {
  if (!customerStore.selectedCustomer || !customerStore.selectedCustomer?.salonerClientId) {
    console.error('Deletion failed: No selected customer or missing customer ID.')
    showToast(t('error'), 'error')

    return
  }

  const messageText = `
    ${t('confirm_client_removal')}
    ${customerStore.selectedCustomer.name ?? ''}
    ${customerStore.selectedCustomer.surname ?? ''}
    ${customerStore.selectedCustomer.phone ? '('+formatPhone(customerStore.selectedCustomer.phone)+')' : ''}
    ${customerStore.selectedCustomer.additionalPhone ? '<br>('+formatPhone(customerStore.selectedCustomer.additionalPhone)+')' : ''}
  `
  const confirmDelete = await modalStore.openConfirmationModal(messageText, t('warning'), { confirmText: t('remove_client'), confirmColor: 'error' })

  if (confirmDelete) {
    try {
      const clientId = customerStore.selectedCustomer.salonerClientId

      await customerStore.deleteCustomerPermanently([clientId])

      showToast(t('successfully_removed'), 'success')
      searchStore.removeFromHistory(clientId)

      // Reload the customer table - IF ON CUSTOMER TAB
      if (route.path === '/clients') {
        await customerStore.loadCustomers()
      }

      customerStore.clearSelectedCustomer()
      modalStore.closeModal(THIS_MODAL_NAME)

    } catch (error) {
      console.error('Error deleting customer:', error)
      showToast(t('customer_delete_error'), 'error')
    }
  }
}
// 👉 Blacklist check and handle
const isBlacklisted = computed(() => {
  return customerStore.selectedCustomer?.blacklisted === 1 ||
    customerStore.selectedCustomer?.blacklisted === '1'
})

onMounted(async () => {
  // 👉 GET AND SELECTED CUSTOMER
  const response = await customerStore.getCustomerInformation(customerStore.selectedCustomer?.salonerClientId)
  if(response.success !== true) {
    modalStore.closeModal(THIS_MODAL_NAME)
    // If modal was opened from browser URL param -> clean this param
    if (route.query?.clientId) {
      router.replace({
        query: {
          ...route.query,
          clientId: undefined,
        },
      })
    }

    return
  }

  // Pass data to localCustomerData for editing&saving
  localCustomerData.value = customerStore.selectedCustomer

  try {
    if (modalStore.focusAfterClose.length) {
      document.getElementById(modalStore.focusAfterClose).focus()
      modalStore.focusAfterClose = ""
    }

  } catch (e) {
  }
  focusTab(0)
})

onBeforeUnmount(() => {
  // console.log('czysciciel1', modalStore.modals.calendarEvent, modalStore.modals.calendarSales)
  // // Clear selected customer if this modal was opened from search bar
  // // If we don't clean it that way, when we want to add new event, current customer will be already selected
  // if (!modalStore.modals.calendarEvent && !modalStore.modals.calendarSales) {
  //   console.log('czysciciel', modalStore.modals.calendarEvent, modalStore.modals.calendarSales)
  //   customerStore.clearSelectedCustomer()
  // }

  eventStore.allowResetEvent = true
  // Needed when we canceled already opened visit
  if (!modalStore.modals.calendarEvent && !modalStore.modals.calendarSales) {
    eventStore.resetSelectedEvent()
  }

  // If modal was opened from browser URL param -> clean this param
  if (route.query?.clientId) {
    router.replace({
      query: {
        ...route.query,
        clientId: undefined,
      },
    })
  }

})
</script>

<template>
  <VDialog
    class="customer-card-dialog"
    :max-width="!mobile? 1680 : ''"
    scrollable
    :model-value="modalStore.modals.customerCardDialog"
    height="100%"
    max-height="1080px"
    :persistent="customerStore.isLoading"
    :fullscreen="mobile"
    @update:model-value="(val) => modalStore.modals.customerCardDialog = val"
  >
    <VCard :loading="customerStore.isLoading">
      <template #title>
        <div class="d-flex justify-space-between mb-4">
          <div class="d-flex flex-wrap ga-1">
            {{ $t('customer_card') }}:
            <CustomerNameSurnamePhone
              :key="customerStore.selectedCustomer?.userId"
              :customer-object="customerStore.selectedCustomer"
            />
          </div>
          <VIcon
            icon="ri-close-line"
            @click="modalStore.closeModal(THIS_MODAL_NAME)"
          />
        </div>
        <VChip
          v-if="isBlacklisted"
          density="compact"
          color="error"
          size="x-large"
          class="mb-4 elevation-5"
          data-testid="client_card_client_blacklisted_indicator"
        >
          <VIcon
            icon="ri-alarm-warning-fill"
            class="mt-n1 mr-2"
          />
          <span class="text-high-emphasis">
            {{ $t('blacklisted') }}
          </span>
        </VChip>
        <VTabs
          v-model="tabs"
          class="customer-card-tabs mt-2 mt-md-0"
          :disabled="customerStore.isLoading"
          :show-arrows="mobile"
          mandatory
          next-icon="ri-arrow-right-s-line"
          prev-icon="ri-arrow-left-s-line"
          @update:model-value="focusTab"
        >
          <VTab :value="0">
            {{ $t('data') }}
          </VTab>
          <VTab :value="1">
            {{ $t('history') }}
          </VTab>
          <VTab :value="2">
            <span> {{ $t('attachments') }}</span>
            <VBadge
              v-if="customerStore.attachmentCount > 0"
              :content="customerStore.attachmentCount"
              inline
            />
          </VTab>

          <VTab :value="3">
            {{ $t('vouchers') }}
            <VBadge
              v-if="customerStore.gatheredUserInfo?.vouchers?.giftCardsCount > 0"
              :content="customerStore.gatheredUserInfo?.vouchers?.giftCardsCount"
              inline
            />
          </VTab>
          <VTab :value="4">
            {{ $t('tickets') }}
            <VBadge
              v-if="customerStore.gatheredUserInfo?.vouchers?.passesCount > 0"
              :content="customerStore.gatheredUserInfo?.vouchers?.passesCount"
              inline
            />
          </VTab>

          <VTab
            :value="5"
            :disabled="!customerStore.selectedCustomer?.salonerClientId"
          >
            {{ $t('messages') }}
          </VTab>

          <VTab
            v-if="calendarStore.integrations.beautyDoc?.enabled"
            :value="6"
            :disabled="!customerStore.selectedCustomer?.salonerClientId || !customerStore.selectedCustomer.phone || !customerStore.selectedCustomer.name || !customerStore.selectedCustomer.surname"
          >
            BeautyDoc
          </VTab>
        </VTabs>
      </template>

      <template #text>
        <!-- The conversation fills the card (its messages scroll), other tabs scroll in the card -->
        <VTabsWindow
          v-model="tabs"
          :class="{ 'h-100': tabs === 5 }"
        >
          <!-- 👉 DATA -->
          <VTabsWindowItem
            :ref="el => tabRefs[0].value = el"
            tabindex="0"
            :value="0"
          >
            <!-- Customer data component -->

            <VRow>
              <VCol
                cols="12"
                md="6"
                class="pr-lg-5 pt-8"
                :class="mobile ? 'pb-0' : 'pseudo-right-divider'"
              >
                <SectionDivider
                  :text="$t('data')"
                  class="mb-4"
                />
                <CustomerData
                  ref="customerDataComponent"
                  :customer-data="localCustomerData"
                />
              </VCol>
              <!--              <VDivider-->
              <!--                v-if="!mobile"-->
              <!--                vertical-->
              <!--              />-->
              <VCol
                cols="12"
                md="6"
                class="pl-lg-5 pt-8"
              >
                <!-- 👉 STATISTICS-->
                <SectionDivider
                  :text="$t('statistics')"
                  class="mb-4"
                />

                <UserStatistics
                  :key="`${customerStore.gatheredUserInfo?.loyaltyProgram.loyaltyPoints}-${customerStore.voucherCount}-${customerStore.ticketCount}`"
                  @update:tabs="tabs = $event"
                />
              </VCol>
            </VRow>
          </VTabsWindowItem>
          <!-- 👉 VISITS HISTORY-->
          <VTabsWindowItem
            :ref="el => tabRefs[1].value = el"
            tabindex="0"
            :value="1"
            class="pt-2"
          >
            <CustomerHistoryVisits />
          </VTabsWindowItem>
          <!-- 👉 ATTACHMENTS-->
          <VTabsWindowItem
            :ref="el => tabRefs[2].value = el"
            tabindex="0"
            :value="2"
            class="pt-4 overflow-x-hidden"
          >
            <FileUploader density="compact" />
          </VTabsWindowItem>

          <!-- 👉 VOUCHERS-->
          <VTabsWindowItem
            :ref="el => tabRefs[3].value = el"
            tabindex="0"
            :value="3"
            class="pt-2"
          >
            <VouchersTable />
          </VTabsWindowItem>
          <!-- 👉 TICKETS -->
          <VTabsWindowItem
            :ref="el => tabRefs[4].value = el"
            tabindex="0"
            :value="4"
            class="pt-2"
          >
            <TicketsTable />
          </VTabsWindowItem>
          <VTabsWindowItem
            :ref="el => tabRefs[5].value = el"
            tabindex="0"
            :value="5"
            class="pt-2 h-100"
          >
            <!-- 👉 MESSAGES -->
            <!-- v-if refetches the conversation on every entry -->
            <ConversationThread
              v-if="tabs === 5"
              :key="conversationClient.id"
              :client="conversationClient"
              :disabled="!customerStore.selectedCustomer?.hasValidPhone"
              embedded
              show-notice
              class="h-100 border rounded"
            />
          </VTabsWindowItem>
          <VTabsWindowItem
            :ref="el => tabRefs[6].value = el"
            tabindex="0"
            :value="6"
            class="pt-2"
          >
            <!-- 👉 BEAUTYDOC -->
            <!-- v-if remounts the tab on every entry, so the loader replays -->
            <BeautyDocTab
              v-if="tabs === 6"
              full
            />
          </VTabsWindowItem>
        </VTabsWindow>
      </template>
      <VCardActions class="border-t pt-2">
        <div class="d-flex pa-2 justify-space-between w-100 ">
          <div class="d-flex ga-2">
            <VBtn
              color="secondary"
              variant="outlined"
              @click="modalStore.closeModal(THIS_MODAL_NAME)"
            >
              {{ $t('cancel') }}
            </VBtn>

            <VBtn
              v-if="calendarStore.viewSettings.allowClientsRemoval"
              variant="text"
              color="secondary"
              prepend-icon="ri-delete-bin-6-line"
              :disabled="customerStore.isLoading"
              @click="handleCustomerDelete"
            >
              {{ $t('deleteCustomer') }}
            </VBtn>
          </div>

          <div v-if="tabs === 0">
            <VBtn
              color="green"
              variant="flat"
              :disabled="customerStore.isLoading"
              @click="submitCustomer"
            >
              <span>{{ $t('save') }}</span>
            </VBtn>
          </div>
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
  <EditDateDialog v-if="modalStore.modals.editDate" />
</template>

<style lang="scss">
.customer-card-tabs {
  .v-slide-group__next,
  .v-slide-group__prev {
    width: 30px !important;
    min-width: 30px !important;
  }

  .v-slide-group__next {
    justify-content: flex-end;
  }

  .v-slide-group__prev {
    justify-content: flex-start;
  }
}

.customer-card-dialog {
  .v-card-text {
    padding-top: 0 !important;
  }
}
</style>
